import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Save, Trash2 } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const iconBtnCls = "border border-white/10 p-1.5 text-zinc-400 hover:text-zinc-100 hover:border-white/30 transition-colors duration-300";

export default function AdminFab() {
  const [products, setProducts] = useState(null);

  const load = () => api.get("/fab/products").then((r) => setProducts(r.data)).catch(() => toast.error("Failed to load products"));
  useEffect(() => { load(); }, []);

  const update = (i, field, value) => setProducts((ps) => ps.map((p, j) => (j === i ? { ...p, [field]: value } : p)));

  const save = async (product) => {
    try {
      await api.put(`/fab/products/${product.id}`, product);
      toast.success(`Saved: ${product.name}`);
    } catch {
      toast.error("Failed to save product");
    }
  };

  const remove = async (product) => {
    try {
      await api.delete(`/fab/products/${product.id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Failed to delete product");
    }
  };

  const add = async () => {
    try {
      await api.post("/fab/products", { name: "New Asset Pack", price: "$0.00", discount: "", thumbnail: "", url: "" });
      load();
    } catch {
      toast.error("Failed to add product");
    }
  };

  return (
    <section data-testid="admin-fab-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-nc-blue flex items-center gap-2">
          <Package className="w-4 h-4" /> Fab Asset Catalog
        </h2>
        <button data-testid="admin-fab-add-button" onClick={add} className="font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 hover:bg-nc-blue/10 px-3 py-2 flex items-center gap-2 transition-colors duration-300">
          <Plus className="w-3.5 h-3.5" /> Add Product
        </button>
      </div>
      <div className="p-6 flex flex-col gap-3">
        {!products && <p className="font-mono text-xs text-zinc-500 uppercase tracking-[0.25em] animate-pulse">Loading catalog...</p>}
        {(products || []).map((p, i) => (
          <div key={p.id} data-testid={`admin-fab-${i + 1}`} className="border border-white/10 p-4 bg-[#050505]/50 grid grid-cols-1 lg:grid-cols-[64px_2fr_100px_90px_2fr_auto] gap-3 items-center">
            {p.thumbnail ? (
              <img src={p.thumbnail} alt="" className="w-16 h-16 object-cover border border-white/10" />
            ) : (
              <div className="w-16 h-16 border border-white/10 flex items-center justify-center"><Package className="w-5 h-5 text-zinc-700" /></div>
            )}
            <div>
              <label className={labelCls}>Name</label>
              <input data-testid={`admin-fab-${i + 1}-name-input`} className={inputCls} value={p.name} onChange={(e) => update(i, "name", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Price</label>
              <input data-testid={`admin-fab-${i + 1}-price-input`} className={inputCls} value={p.price} onChange={(e) => update(i, "price", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Discount</label>
              <input data-testid={`admin-fab-${i + 1}-discount-input`} className={inputCls} value={p.discount} onChange={(e) => update(i, "discount", e.target.value)} placeholder="-50%" />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <input data-testid={`admin-fab-${i + 1}-url-input`} className={inputCls} value={p.url} onChange={(e) => update(i, "url", e.target.value)} placeholder="Fab listing URL" />
              <input data-testid={`admin-fab-${i + 1}-thumbnail-input`} className={inputCls} value={p.thumbnail} onChange={(e) => update(i, "thumbnail", e.target.value)} placeholder="Thumbnail URL" />
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button data-testid={`admin-fab-${i + 1}-save-button`} onClick={() => save(p)} className={`${iconBtnCls} hover:text-nc-green`} title="Save product">
                <Save className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`admin-fab-${i + 1}-delete-button`} onClick={() => remove(p)} className={`${iconBtnCls} hover:text-red-400`} title="Delete product">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
